import axiosInstance from "../lib/axios.instance";
import { getAccessToken } from "../utils/storage.utils";
import { encodePathSegment } from "./path.utils";
import { debugError, debugLog } from "@/utils/debug";
import type {
  ICompasAIQueryRequest,
  ICompasAIActionsRequest,
  IActionRunResponse,
  ICompasAIChatHistoryResponse,
  ICompasAIChatAction,
  INextActionResponse,
  IActionOutputResponse,
  IReorderActionsRequest,
  IReorderActionsResponse,
  IEditActionPromptRequest,
  IAnonymizePromptRequest,
  IAnonymizePromptResponse,
  IChatMessagesChainRequest,
  IChatMessagesChainResponse,
} from "../types/compass.types";

const BASE_URL = "compas-ai";

export const compasAIQueryStream = async (
  payload: ICompasAIQueryRequest,
  signal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
  const rawUrl = axiosInstance.defaults.baseURL ?? "https://api-dev.legalassistant.au";
  const apiUrl = rawUrl.replace(/\/$/, "");
  const token = await getAccessToken();

  if (token == null) {
    debugError("compass", "stream blocked: no access token");
    throw new Error("No access token available");
  }

  const url = `${apiUrl}/${BASE_URL}/converse/stream`;
  debugLog("compass", "opening stream", { url, hasDocument: payload.current_document_id !== "" });

  // When the caller owns the deadline, reuse that signal so fetch + body
  // share one 90s budget. Otherwise time out the headers-only wait.
  const controller = new AbortController();
  const timeoutId =
    signal == null ? window.setTimeout(() => controller.abort(), 90_000) : undefined;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal: signal ?? controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("Compass took too long to reply. Please try again.");
    }
    throw error;
  } finally {
    if (timeoutId != null) window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    debugError("compass", "stream HTTP error", { status: response.status });
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (response.body == null) {
    throw new Error("Response body is null");
  }

  return response.body;
};

export const getCompassChatHistory = async (
  documentId: string
): Promise<ICompasAIChatHistoryResponse> => {
  const response = await axiosInstance.get<ICompasAIChatHistoryResponse>(`${BASE_URL}/history`, {
    params: { context_id: documentId },
  });
  return response.data;
};

export const runCompassActions = async (
  payload: ICompasAIActionsRequest | ICompasAIActionsRequest[]
): Promise<IActionRunResponse> => {
  const response = await axiosInstance.post<IActionRunResponse>(`${BASE_URL}/actions/run`, payload);
  return response.data;
};

export const getNextAction = async (documentId: string): Promise<INextActionResponse> => {
  const response = await axiosInstance.get<INextActionResponse>(`${BASE_URL}/queue/next`, {
    params: { context_id: documentId },
  });
  return response.data;
};

export const getAllQueuedActions = async (documentId: string): Promise<ICompasAIChatAction[]> => {
  const response = await axiosInstance.get<ICompasAIChatAction[]>(`${BASE_URL}/actions`, {
    params: { context_id: documentId },
  });
  return response.data;
};

export const cancelQueuedActionById = async (
  documentId: string,
  jobIds: string[]
): Promise<void> => {
  const params =
    documentId !== "" && jobIds.length > 0
      ? { context_id: documentId, item_ids: jobIds.join(",") }
      : undefined;
  await axiosInstance.post(`${BASE_URL}/queue/cancel`, undefined, { params });
};

export const getActionsStatus = async (itemIds: string[]): Promise<Record<string, string>> => {
  const response = await axiosInstance.post<Record<string, string>>(
    `${BASE_URL}/queue/status`,
    itemIds
  );
  return response.data;
};

export const rerunFailedAction = async (itemId: string): Promise<void> => {
  await axiosInstance.post(`${BASE_URL}/actions/rerun/${encodePathSegment(itemId)}`);
};

export const getActionOutput = async (itemId: string): Promise<IActionOutputResponse> => {
  const response = await axiosInstance.get<IActionOutputResponse>(
    `${BASE_URL}/actions/output/${encodePathSegment(itemId)}`
  );
  return response.data;
};

export const reorderActions = async (
  payload: IReorderActionsRequest
): Promise<IReorderActionsResponse> => {
  const { context_id, ...requestBody } = payload;
  const response = await axiosInstance.post<IReorderActionsResponse>(
    `${BASE_URL}/history/reorder-actions`,
    requestBody,
    { params: { context_id } }
  );
  return response.data;
};

export const editActionPrompt = async (
  actionId: string,
  contextId: string,
  prompt: string
): Promise<void> => {
  const payload: IEditActionPromptRequest = { prompt };
  await axiosInstance.post(
    `${BASE_URL}/actions/edit-prompt/${encodePathSegment(actionId)}`,
    payload,
    { params: { context_id: contextId } }
  );
};

export const deleteAction = async (actionId: string, contextId: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/actions/${encodePathSegment(actionId)}`, {
    params: { context_id: contextId },
  });
};

export const anonymizePrompt = async (
  payload: IAnonymizePromptRequest
): Promise<IAnonymizePromptResponse> => {
  const response = await axiosInstance.post<IAnonymizePromptResponse>(
    `${BASE_URL}/anonymize-prompt`,
    payload
  );
  return response.data;
};

export const chatMessagesChain = async (
  payload: IChatMessagesChainRequest
): Promise<IChatMessagesChainResponse> => {
  const response = await axiosInstance.post<IChatMessagesChainResponse>(
    `ai/chat/messages/chain`,
    payload
  );
  return response.data;
};
